import React, { useEffect, useState } from 'react';
import { Modal, Button, Row, Col, Card, Table, Spinner, Badge } from 'react-bootstrap';
import { FaWindowClose } from 'react-icons/fa';
import moment from 'moment';
import { toast } from 'react-toastify';
import styles from '../styles.module.css';
import { chavesContasPagarImovel } from '~/Services/GestaoIPTUService';
import { extrairMensagemErro } from '~/Utils/Helpers';

const COR_PRIMARIA = '#001f5b';
const COR_LABEL = '#8e9aab';
const COR_DIVIDER = 'rgba(0, 31, 91, 0.08)';
const COR_TABELA_HEADER_BG = '#f3f5fa';

const formatarData = (valor) => {
  if (!valor) return '—';
  const d = moment(valor);
  return d.isValid() ? d.format('DD/MM/YYYY') : '—';
};

const formatarMoeda = (valor) => {
  if (valor == null) return '—';
  const numero = Number(valor);
  if (Number.isNaN(numero)) return '—';
  return numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const movChipClass = (movTipo) => {
  if (movTipo === 1) return styles.movChip1;
  if (movTipo === 2) return styles.movChip2;
  if (movTipo === 3) return styles.movChip3;
  if (movTipo === 4) return styles.movChip4;
  if (movTipo === 5) return styles.movChip5;
  return '';
};

const rotuloStyle = {
  fontSize: 10.5,
  fontWeight: 700,
  color: COR_LABEL,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 2,
};

const valorStyle = { fontSize: 14, fontWeight: 500, color: '#212529' };
const vazioStyle = { fontSize: 13, fontStyle: 'italic', color: '#adb5bd' };

const Campo = ({ label, valor, children, full }) => (
  <Col xs={12} sm={full ? 12 : 6}>
    <div style={rotuloStyle}>{label}</div>
    <div style={valorStyle}>
      {children
        || (valor != null && valor !== ''
          ? valor
          : <span style={vazioStyle}>—</span>)}
    </div>
  </Col>
);

const Grupo = ({ children, last }) => (
  <Row
    className="mx-0"
    style={{
      paddingTop: 14,
      paddingBottom: 14,
      borderBottom: last ? 'none' : `1px solid ${COR_DIVIDER}`,
      rowGap: 12,
    }}
  >
    {children}
  </Row>
);

const ModalDetalhesImovel = ({ show, onHide, imovel }) => {
  const [chaves, setChaves] = useState([]);
  const [carregandoChaves, setCarregandoChaves] = useState(false);

  useEffect(() => {
    if (!show || !imovel) return undefined;
    let ativo = true;
    setCarregandoChaves(true);
    chavesContasPagarImovel(imovel.codigo_ativo)
      .then((res) => {
        if (ativo) setChaves(Array.isArray(res) ? res : []);
      })
      .catch((err) => {
        if (ativo) toast.error(extrairMensagemErro(err));
      })
      .finally(() => {
        if (ativo) setCarregandoChaves(false);
      });
    return () => { ativo = false; };
  }, [show, imovel]);

  if (!imovel) return null;

  const qdLt = imovel.quadra || imovel.lote
    ? `${imovel.quadra || ''}${imovel.lote ? `/${imovel.lote}` : ''}`
    : null;

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton style={{ borderBottom: `1px solid ${COR_DIVIDER}` }}>
        <Modal.Title style={{ fontSize: 18, color: COR_PRIMARIA, fontWeight: 600 }}>
          Outras Informações
          <span className="ml-2" style={{ display: 'inline-flex', gap: 6, verticalAlign: 'middle' }}>
            <Badge
              variant="light"
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: COR_PRIMARIA,
                backgroundColor: '#eef2f9',
                padding: '6px 10px',
              }}
            >
              Código {imovel.codigo_ativo}
            </Badge>
            {qdLt && (
              <Badge
                variant="light"
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: COR_PRIMARIA,
                  backgroundColor: '#eef2f9',
                  padding: '6px 10px',
                }}
              >
                QD/LT {qdLt}
              </Badge>
            )}
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ backgroundColor: '#fafbfd', paddingTop: 16, paddingBottom: 16 }}>
        <Card className="shadow-sm" style={{ border: 'none' }}>
          <Card.Body className="py-1 px-3">
            <Grupo>
              <Campo label="Cadastro Prefeitura" valor={imovel.cad_prefeitura} />
              <Campo label="Transferência" valor={imovel.transf} />
            </Grupo>

            <Grupo>
              <Campo label="Tipo">
                {imovel.mov_tipo != null ? (
                  <span className="d-inline-flex align-items-center">
                    <span className={`${styles.movChip} ${movChipClass(imovel.mov_tipo)}`} />
                    {imovel.mov_tipo}
                  </span>
                ) : <span style={vazioStyle}>—</span>}
              </Campo>
              <Campo label="Descrição do Tipo" valor={imovel.nome_tipo} />
            </Grupo>

            <Grupo>
              <Campo label="Status Carnê" valor={imovel.status_carne} />
              <Campo label="Motivo Status Pagamento" valor={imovel.motivo_status_carne} />
            </Grupo>

            <Grupo>
              <Campo label="Opção Pagamento" valor={imovel.opcao_pagamento} />
              <Campo label="Motivo do Pagamento" valor={imovel.motivo_pagamento} />
            </Grupo>

            <Grupo>
              <Campo label="Opção Condomínio" valor={imovel.opcao_condominio} />
              <Campo label="Motivo do Condomínio" valor={imovel.motivo_condominio} />
            </Grupo>

            <Grupo>
              <Campo label="Ordem de Pagamento" valor={imovel.ordem_pagamento} />
              <Campo label="Motivo da Ordem de Pagamento" valor={imovel.motivo_ordem_pagamento} />
            </Grupo>

            <Grupo>
              <Campo label="Observação do Bloqueio" valor={imovel.obs} full />
            </Grupo>

            <Grupo last>
              <Col xs={12}>
                <div
                  className="d-flex align-items-center justify-content-between"
                  style={{ marginBottom: 8 }}
                >
                  <div style={rotuloStyle}>Chave Contas a Pagar</div>
                  {!carregandoChaves && (
                    <Badge
                      variant="light"
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: COR_PRIMARIA,
                        backgroundColor: '#eef2f9',
                      }}
                    >
                      {chaves.length} {chaves.length === 1 ? 'chave' : 'chaves'}
                    </Badge>
                  )}
                </div>
                {carregandoChaves ? (
                  <div className="text-center py-3">
                    <Spinner animation="border" variant="primary" size="sm" />
                  </div>
                ) : (
                  <Table
                    hover
                    size="sm"
                    style={{
                      fontSize: 13,
                      marginBottom: 0,
                      border: `1px solid ${COR_DIVIDER}`,
                    }}
                  >
                    <thead style={{ backgroundColor: COR_TABELA_HEADER_BG }}>
                      <tr>
                        <th
                          style={{
                            color: COR_PRIMARIA,
                            fontSize: 11,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            border: 'none',
                          }}
                        >
                          Chave
                        </th>
                        <th
                          style={{
                            color: COR_PRIMARIA,
                            fontSize: 11,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            border: 'none',
                            width: 140,
                            textAlign: 'center',
                          }}
                        >
                          Vencimento
                        </th>
                        <th
                          style={{
                            color: COR_PRIMARIA,
                            fontSize: 11,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            border: 'none',
                            width: 160,
                            textAlign: 'right',
                          }}
                        >
                          Valor
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {chaves.length === 0 ? (
                        <tr>
                          <td
                            colSpan={3}
                            className="text-center py-3"
                            style={vazioStyle}
                          >
                            Nenhuma chave encontrada.
                          </td>
                        </tr>
                      ) : (
                        chaves.map((c) => (
                          <tr key={c.chave}>
                            <td style={{ fontFamily: 'monospace' }}>{c.chave}</td>
                            <td style={{ textAlign: 'center' }}>{formatarData(c.vencimento)}</td>
                            <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                              {formatarMoeda(c.valor)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                )}
              </Col>
            </Grupo>
          </Card.Body>
        </Card>
      </Modal.Body>

      <Modal.Footer style={{ borderTop: `1px solid ${COR_DIVIDER}` }}>
        <Button
          variant="light"
          className="d-inline-flex align-items-center"
          onClick={onHide}
        >
          <FaWindowClose />
          <span className="ml-2 d-xs-none">Fechar</span>
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalDetalhesImovel;
